import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useLogout } from '../../../hooks/useAuth';
import { formatCurrency } from '../../../utils/format';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../ui/DropdownMenu';
import {
  ChevronDown20Filled,
  Person20Filled,
  Person24Filled,
  Search20Filled,
  Wallet16Filled,
} from '@fluentui/react-icons';
import { useUserDetails } from '../../../hooks/useAuth';
import {
  User,
  Settings,
  LogOut,
  Plus,
  Package,
  ShieldCheck,
  FileText,
} from 'lucide-react';
import { Spinner } from '../../ui/Spinner';
import { useNavigate, useLocation } from 'react-router-dom';
import RechargeWallet from '../../RechargeWallet';
import { useModal } from '../../../hooks/useModal';
import { useOrders } from '../../../hooks/useOrder';
import { OrderCard } from '../../OrderCard';
import { B2BOrderCard } from '../../dashboard/b2b/B2BOrderCard';
import { ordersService } from '../../../services/orders';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useDebounce } from '../../../hooks/useDebounce';
import { useWallet } from '../../../hooks/useWallet';
import { transporterList } from '../../../constants/TransportedId';
import { SimpleTransporterIdCard } from '../../SimpleTransporterIdCard';

interface TopbarProps {
  onMenuClick?: () => void;
}

export const Topbar: React.FC<TopbarProps> = ({ onMenuClick }) => {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const { data: userDetails, isLoading: isLoadingUser } = useUserDetails(
    authUser?.id || ''
  );
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [isWalletHover, setIsWalletHover] = useState(false);
  const { open, close, Modal } = useModal();
  const user = userDetails || authUser;
  const isAdminUser = Number(user?.roleId) === 1 || Number(user?.roleId) === 3;
  const location = useLocation();

  const isB2B = location.pathname.startsWith('/b2b');

  const { balance, isLoadingBalance, isPostpaid } = useWallet(user?.id as string, isB2B);

  const debouncedSearch = useDebounce(searchValue, 500);
  const hasSearch = debouncedSearch.trim().length > 2;
  const { orders: searchResults, loading: isLoadingOrders } = useOrders({
    userId: user?.id || '',
    storeId: String(user?.storeId || 'all'),
    shipmentStatus: 'all',
    page: 1,
    pageSize: 10,
    searchTerm: hasSearch ? debouncedSearch : undefined,
    enabled: hasSearch && !isB2B,
    isAdmin: user?.roleName === 'ADMIN',
  });

  const [b2bSearchResults, setB2bSearchResults] = useState<any[]>([]);
  const [isB2bLoading, setIsB2bLoading] = useState(false);

  useEffect(() => {
    if (isB2B && hasSearch && user?.id) {
      setIsB2bLoading(true);
      ordersService.getB2bOrders(user.id, user?.roleId, { search: debouncedSearch, page: 1, pageSize: 10, tab: 'all' })
        .then(res => {
          const mapped = (res.orders || []).map((o: any) => ({
            id: o.id,
            trackingId: o.awbNumber || o.id,
            customer: o.consigneeName || 'N/A',
            departure: o.senderCity || 'N/A',
            dDate: o.createdAt ? new Date(o.createdAt).toLocaleDateString() : 'N/A',
            arrival: o.consigneeCity || 'N/A',
            aDate: o.edd ? new Date(o.edd).toLocaleDateString() : 'N/A',
            owner: o.companyName || 'N/A',
            weight: o.chargeableWeight ? `${o.chargeableWeight} kg` : (o.actualWeight ? `${o.actualWeight} kg` : '-'),
            status: o.status || 'Created',
          }));
          setB2bSearchResults(mapped);
        })
        .catch(err => console.error(err))
        .finally(() => setIsB2bLoading(false));
    } else {
      setB2bSearchResults([]);
    }
  }, [isB2B, hasSearch, debouncedSearch, user?.id, user?.roleId]);

  const activeSearchResults = isB2B ? b2bSearchResults : searchResults;
  const activeIsLoading = isB2B ? isB2bLoading : isLoadingOrders;

  const logoutMutation = useLogout();

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
      await logout();
    }
  };

  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 150);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      dropdownContainerRef.current &&
      !dropdownContainerRef.current.contains(e.target as Node)
    ) {
      setIsDropdownOpen(false);
    }
    if (
      searchContainerRef.current &&
      !searchContainerRef.current.contains(e.target as Node)
    ) {
      setIsSearchDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const openRechargeWallet = () => {
    open({
      Component: () => <RechargeWallet closeModal={close} />,
      centered: true,
      closable: true,
      className: 'w-full max-w-[420px] rounded-2xl z-[60]',
    });
  };

  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // Show loading state or fallback while data is being fetched
  if (isLoadingUser && !user) {
    return (
      <header className="py-4 border-b bg-foreground px-4 lg:px-6 flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="hidden xl:block flex-1 max-w-md">
            <div className="relative">
              <Search20Filled className="absolute left-3 top-1/2 transform -translate-y-1/2 text-teal" />
              <Input
                type="search"
                placeholder="Search parcels, orders..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="animate-pulse bg-gray-200 rounded-md w-24 h-8"></div>
          <div className="animate-pulse bg-gray-200 rounded-full w-8 h-8"></div>
        </div>
      </header>
    );
  }

  return (
    <header className="py-4 border-b bg-foreground px-4 lg:px-6 flex items-center justify-between relative">
      {/* Mobile Search Overlay */}
      <AnimatePresence>
        {isMobileSearchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute inset-0 bg-white z-[110] px-4 flex items-center gap-3"
          >
            <div className="flex-1 relative">
              <Input
                autoFocus
                type="search"
                placeholder="Search Tracking ID Order No."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-10 h-11 border-none bg-gray-50"
                endEnhancer={<Search20Filled className="text-teal" />}
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setIsMobileSearchOpen(false);
                setSearchValue('');
              }}
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Mobile Search Results Dropdown inside Overlay */}
            <AnimatePresence>
              {searchValue.trim().length > 2 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full left-0 right-0 mt-2 bg-white border border-muted rounded-xl shadow-2xl z-[100] max-h-[70vh] overflow-y-auto p-4"
                >
                  <div className="flex items-center justify-between mb-4 px-2">
                    <h3 className="text-sm font-bold text-text tracking-wider">
                      Search Results
                    </h3>
                  </div>

                  {activeIsLoading ? (
                    <div className="flex flex-col items-center py-10 gap-3">
                      <Spinner size="lg" className="text-teal" />
                      <p className="text-sm text-text3 italic">Searching...</p>
                    </div>
                  ) : activeSearchResults && activeSearchResults.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4">
                      {activeSearchResults.map((order: any) => (
                        <div
                          key={order.order_id || order.id}
                          onClick={() => {
                            setSearchValue('');
                            setIsMobileSearchOpen(false);
                            if (isB2B) {
                              navigate('/b2b/orders'); // Basic navigation for now, B2B doesn't use selectedOrder state the same way
                            } else {
                              navigate('/orders', {
                                state: { selectedOrder: order },
                              });
                            }
                          }}
                        >
                          {isB2B ? (
                            <B2BOrderCard
                              order={{ ...order, isSelected: false }}
                              onSelectChange={() => {}}
                            />
                          ) : (
                            <OrderCard order={order} onSelect={() => {}} />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-10 text-center">
                      <p className="text-text3 italic">No orders found</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Left side - Logo/Search */}
      <div className="flex items-center gap-4 flex-1">
        <div
          ref={searchContainerRef}
          className="hidden xl:block flex-1 max-w-md relative"
        >
          <div className="relative">
            <Input
              type="search"
              placeholder="Search Tracking ID, Order No."
              value={searchValue}
              onChange={(e) => {
                const val = e.target.value;
                setSearchValue(val);
                if (val.trim().length > 2) {
                  setIsSearchDropdownOpen(true);
                } else {
                  setIsSearchDropdownOpen(false);
                }
              }}
              onFocus={() => {
                if (searchValue.trim().length > 2) {
                  setIsSearchDropdownOpen(true);
                }
              }}
              className="pl-10 h-11"
              endEnhancer={<Search20Filled className=" text-teal" />}
            />
          </div>

          {/* Search Results Dropdown (Desktop) */}
          <AnimatePresence>
            {isSearchDropdownOpen && searchValue.trim().length > 2 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute top-full left-0 right-0 mt-2 bg-white border border-muted rounded-2xl shadow-2xl z-[100] max-h-[500px] overflow-y-auto p-4 scrollbar-thin"
              >
                <div className="flex items-center justify-between mb-4 px-2">
                  <h3 className="text-sm font-bold text-text  tracking-wider">
                    Search Results
                  </h3>
                  <button
                    onClick={() => {
                      setSearchValue('');
                      setIsSearchDropdownOpen(false);
                    }}
                    className="text-text3 hover:text-text"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {activeIsLoading ? (
                  <div className="flex flex-col items-center py-10 gap-3">
                    <Spinner size="lg" className="text-teal" />
                    <p className="text-sm text-text3 italic">
                      Searching for orders...
                    </p>
                  </div>
                ) : activeSearchResults && activeSearchResults.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4">
                    {activeSearchResults.map((order: any) => (
                      <div
                        key={order.order_id || order.id}
                        onClick={() => {
                          setSearchValue('');
                          setIsSearchDropdownOpen(false);
                          if (isB2B) {
                            navigate('/b2b/orders'); 
                          } else {
                            navigate('/orders', {
                              state: { selectedOrder: order },
                            });
                          }
                        }}
                        className="transform scale-[0.98] origin-center"
                      >
                        {isB2B ? (
                          <B2BOrderCard
                            order={{ ...order, isSelected: false }}
                            onSelectChange={() => {}}
                          />
                        ) : (
                          <OrderCard order={order} onSelect={() => {}} />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-10 text-center">
                    <p className="text-text3 italic">
                      No orders found matching "{searchValue}"
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Right side items */}
      <div className="flex items-center gap-2 sm:gap-4">
        <div className="w-24 lg:w-32 hidden lg:block">
          <Select
            value={isB2B ? 'B2B' : 'B2C'}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'B2B' && !isB2B) {
                window.open('/b2b/dashboard', '_blank');
              } else if (value === 'B2C' && isB2B) {
                window.open('/dashboard', '_blank');
              }
            }}
            options={[
              { label: 'B2C', value: 'B2C' },
              { label: 'B2B', value: 'B2B' },
            ]}
            className="h-10 border-gray-200"
          />
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="xl:hidden"
          onClick={() => setIsMobileSearchOpen(true)}
        >
          <Search20Filled className="text-teal" />
        </Button>

        {user?.active !== 1 && (
          <div className="px-3 py-1 bg-red-50 border border-red-200 relative rounded-lg">
            <p className="text-red-600 font-bold text-xs uppercase">Inactive</p>
          </div>
        )}

        <Button
          onClick={() => {
            if (!isB2B || isAdminUser)
              navigate(isB2B ? '/b2b/orders/add' : '/orders/add');
          }}
          disabled={isB2B && !isAdminUser}
          className={`flex items-center gap-2 bg-white text-teal border border-teal rounded-full px-3 lg:px-5 py-2 h-10 shadow-md ${
            isB2B && !isAdminUser
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-teal/5'
          }`}
        >
          <div className="relative text-teal">
            <Package size={18} />
            <Plus
              size={10}
              className="absolute -top-1 -right-1 bg-teal text-white rounded-full p-0.5 border border-white"
            />
          </div>
          <span className="hidden sm:inline font-bold text-sm">Add Orders</span>
        </Button>

        {user?.roleName === 'ADMIN' && isB2B && (
          <button
            type="button"
            onClick={() => {
              open({
                Component: () => (
                  <div className=" flex flex-col">
                    <div className="flex-shrink-0">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Transporter ID
                      </h3>
                    </div>
                    <div className="overflow-y-auto pr-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {transporterList.map((item, index) => (
                        <SimpleTransporterIdCard
                          key={index}
                          name={item.name}
                          transporterId={item.transporterId}
                        />
                      ))}
                    </div>
                  </div>
                ),
                className: 'max-w-7xl',
                centered: true,
                closable: true,
              });
            }}
            className="flex items-center justify-center gap-2 px-3 xl:px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-full text-sm font-semibold transition-colors border border-blue-200 shadow-sm h-10 w-10 xl:w-auto shrink-0"
            title="Transporter ID"
          >
            <FileText className="w-4 h-4 shrink-0" />
            <span className="hidden xl:inline">Transporter ID</span>
          </button>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="relative hover:bg-gray-100 rounded-full"
        >
          <Bell className="h-5 w-5 text-black" />
          <span className="absolute top-2 right-3 h-2 w-2 bg-destructive rounded-full" />
        </Button>

        {user?.roleName === 'ADMIN' && (
          <span className=" bg-amber-50 border border-amber-600 text-amber-600 rounded-lg py-2 px-2 sm:text-xs md:text-sm">
            Admin
          </span>
        )}

        <div
          className="relative"
          onMouseEnter={() => setIsWalletHover(true)}
          onMouseLeave={() => setIsWalletHover(false)}
        >
          <div
            onClick={!isPostpaid ? openRechargeWallet : undefined}
            className={`sm:flex items-center gap-2 px-4 py-2 bg-background rounded-full transition ${
              !isPostpaid ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'
            }`}
          >
            <Wallet16Filled className="h-4 w-4 text-teal" />
            <span className="hidden lg:block text-base font-bold text-text2">
              {isLoadingBalance ? '...' : formatCurrency(balance || 0)}
            </span>
          </div>

          {isWalletHover && (
            <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 shadow-xl rounded-xl p-4 z-50 animate-in fade-in zoom-in-95 duration-150">
              <div className="flex justify-between text-base mb-2">
                <span className="text-gray-500">Usable Balance</span>
                <span className="font-semibold text-green-600">
                  {isLoadingBalance ? '...' : formatCurrency(balance || 0)}
                </span>
              </div>

              <div className="flex justify-between text-base">
                <span className="text-gray-500">On Hold</span>
                <span className="font-semibold text-red-500">
                  {formatCurrency(0)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Profile dropdown */}
        <div
          className="relative"
          ref={dropdownContainerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <DropdownMenu
            open={isDropdownOpen}
            onOpenChange={setIsDropdownOpen}
            modal={false}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-full hover:bg-gray-50">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 bg-teal-100 rounded-full relative">
                    <Person24Filled className="text-teal" />
                    {user?.active === 1 && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                    )}
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white rounded-2xl w-56 px-4 py-3 shadow-xl border border-gray-100 z-50"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              sideOffset={6}
              collisionPadding={16}
            >
              {/* User info section */}
              <div className="px-2 py-3 mb-2 border-b border-gray-100">
                <div className="flex gap-4 items-center">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email}
                    </p>
                    {user?.crn_number && (
                      <p className="text-xs font-semibold text-gray-500 truncate mt-0.5">
                        CRN: {user.crn_number}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <DropdownMenuItem
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                onClick={() => navigate('/settings/profile')}
              >
                <User className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  Profile
                </span>
              </DropdownMenuItem>

              <DropdownMenuItem
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                onClick={() => navigate('/settings')}
              >
                <Settings className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  Settings
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="my-2 bg-gray-100" />

              <DropdownMenuItem
                onClick={handleLogout}
                disabled={logoutMutation.isPending}
                className="flex items-center justify-between px-3 py-3 rounded-lg hover:bg-red-50 cursor-pointer transition-colors duration-150"
              >
                <div className="flex items-center gap-3">
                  {logoutMutation.isPending ? (
                    <Spinner size="sm" className="text-red-500" />
                  ) : (
                    <LogOut className="h-4 w-4 text-red-500" />
                  )}
                  <span className="text-sm font-medium text-red-600">
                    {logoutMutation.isPending ? 'Logging out...' : 'Logout'}
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Modal />
    </header>
  );
};
